import { useEffect, useMemo } from "react";
import EggVisual from "@/components/EggVisual";

/**
 * OmniUnlimitedCelebrationOverlay — full-screen 🐦‍🔥 celebration when an
 * SSS-tier Omni Unlimited egg hatches. Visually distinct from the rainbow
 * Omni celebration: deep purple/red/gold flame palette, animated phoenix
 * sigil burst, ash particles falling from above.
 *
 * Props:
 *   open    — render the overlay when truthy
 *   hatched — backend hatch result with rarity='omni_unlimited',
 *             { theme, nests, bonus_items, vip_pass_hours, life_ash_drop? }
 *   onClose — fired when the user dismisses (also auto-closes after 9s)
 */
export default function OmniUnlimitedCelebrationOverlay({ open, hatched, onClose }) {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => onClose?.(), 9000);
    return () => clearTimeout(t);
  }, [open, onClose]);

  // 40 ash flakes — slow-falling embers in the brand palette
  const embers = useMemo(() => {
    const palette = ["#D4AF37", "#DC2626", "#4C1D95", "#FCD34D", "#FFFFFF", "#F97316"];
    return Array.from({ length: 40 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 1.6,
      duration: 3.6 + Math.random() * 2.2,
      size: 3 + Math.random() * 4,
      color: palette[i % palette.length],
    }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !hatched) return null;

  const bonuses = hatched.bonus_items || [];
  const vipDays = Math.round((hatched.vip_pass_hours || 0) / 24);
  const lifeAsh = hatched.life_ash_drop ?? 250;

  return (
    <div
      data-testid="omni-unlimited-celebration"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
      onClick={onClose}
    >
      {/* Dark backdrop with radial flame glow */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(220,38,38,0.45) 0%, rgba(76,29,149,0.55) 35%, #050505 80%)",
        }}
      />
      <div className="absolute inset-0 bg-black/40" />

      {/* Ember/ash particle rain */}
      <div className="absolute inset-0 pointer-events-none">
        {embers.map((e) => (
          <div
            key={e.id}
            className="omni-unlimited-ember"
            style={{
              left: `${e.left}%`,
              width: `${e.size}px`,
              height: `${e.size}px`,
              background: e.color,
              animationDelay: `${e.delay}s`,
              animationDuration: `${e.duration}s`,
              boxShadow: `0 0 ${e.size * 2}px ${e.color}`,
            }}
          />
        ))}
      </div>

      {/* Phoenix flame burst — concentric pulsing rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="omni-unlimited-burst" />
        <div className="omni-unlimited-burst" style={{ animationDelay: "0.4s" }} />
        <div className="omni-unlimited-burst" style={{ animationDelay: "0.8s" }} />
      </div>

      {/* Hero card */}
      <div
        data-testid="omni-unlimited-celebration-card"
        className="omni-flash-in relative bg-[#0A0A0A]/95 border-4 px-10 py-8 max-w-lg mx-4 text-center"
        style={{
          borderImage: "linear-gradient(135deg,#D4AF37,#DC2626,#4C1D95,#D4AF37) 1",
          boxShadow: "0 0 60px rgba(212,175,55,0.55), 0 0 120px rgba(220,38,38,0.45)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[#D4AF37] mb-3">
          🐦‍🔥 1 in 20 Override · SSS
        </div>
        <h2
          className="font-display text-4xl md:text-6xl leading-[0.9]"
          style={{
            background: "linear-gradient(135deg,#FCD34D 0%,#DC2626 45%,#A855F7 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textShadow: "0 0 40px rgba(212,175,55,0.4)",
          }}
        >
          OMNI<br/>UNLIMITED
        </h2>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/80 mt-2 mb-5">
          The Phoenix Has Risen
        </div>

        {/* The egg itself, but BIG with a soft glow */}
        <div className="flex justify-center mb-5 relative">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              style={{
                width: 180, height: 180, borderRadius: "50%",
                background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 70%)",
                filter: "blur(8px)",
              }}
            />
          </div>
          <EggVisual
            theme={hatched.theme}
            letterGrade={hatched.letter_grade || "SSS"}
            size={140}
            showRank
          />
        </div>

        {/* Rewards line — laid out as labeled chips */}
        <div className="flex justify-center gap-2 flex-wrap text-sm mb-3">
          <RewardChip icon="🪹" label={`+${hatched.nests} Nests`} />
          <RewardChip icon="💀" label={`+${lifeAsh} Life Ash`} />
          {bonuses.includes("phoenix_crown") && <RewardChip icon="👑" label="+1 Crown" />}
          {bonuses.includes("flux_fruit") && <RewardChip icon="🌀" label="+1 Flux Fruit" />}
          {vipDays > 0 && <RewardChip icon="🎟️" label={`${vipDays}-day VIP`} highlight />}
        </div>

        <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mt-5">
          Holistic Healing · Flying Higher 🐦‍🔥
        </div>
        <button
          data-testid="omni-unlimited-celebration-close"
          onClick={onClose}
          className="mt-5 px-6 py-2 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs hover:bg-yellow-300 transition-colors"
        >
          Claim Your Wings 🦅
        </button>
      </div>
    </div>
  );
}

function RewardChip({ icon, label, highlight }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border " +
        (highlight
          ? "bg-[#D4AF37] text-black border-[#D4AF37]"
          : "bg-black/60 text-white border-white/20")
      }
    >
      <span className="text-base">{icon}</span>
      <span>{label}</span>
    </span>
  );
}
